import { Badge } from "@/components/ui/badge";
import { Link } from "wouter";
import { ArrowRight, Users, Wand2, Image as ImageIcon, Palette, Dog, Type, Maximize, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

export function QuickActions() {
    const quickActions = [
        {
            title: "Remove Person from Photo",
            description: "Delete unwanted people from any photo - our #1 most requested edit",
            icon: Users,
            gradient: "from-purple-500 to-pink-500",
            percentage: "17.6%",
        },
        {
            title: "Image Enhancement & Lighting",
            description: "Fix dark photos, blur, grain, and poor lighting instantly",
            icon: Wand2,
            gradient: "from-amber-500 to-orange-500",
            percentage: "14%",
        },
        {
            title: "Remove Background",
            description: "Transparent backgrounds or replace with any scene",
            icon: ImageIcon,
            gradient: "from-blue-500 to-cyan-500",
            percentage: "12%",
        },
        {
            title: "Color Correction & Recolor",
            description: "Change clothing colors, objects, or fix color balance",
            icon: Palette,
            gradient: "from-pink-500 to-rose-500",
            percentage: "10%",
        },
        {
            title: "Pet & Animal Photo Editor",
            description: "Remove pets, recolor fur, or perfect your animal photos",
            icon: Dog,
            gradient: "from-green-500 to-emerald-500",
            percentage: "10%",
        },
        {
            title: "Add Text & Logo Watermark",
            description: "Add copyright text, logos, or watermarks professionally",
            icon: Type,
            gradient: "from-indigo-500 to-purple-500",
            percentage: "8%",
        },
        {
            title: "Upscale Image to 4K",
            description: "AI super-resolution for crystal clear, high-res photos",
            icon: Maximize,
            gradient: "from-cyan-500 to-teal-500",
            percentage: "6%",
        },
        {
            title: "Photo Restoration Service",
            description: "Restore damaged, old, or faded family photos",
            icon: Clock,
            gradient: "from-violet-500 to-fuchsia-500",
            percentage: "5%",
        },
    ];

    return (
        <section className="py-24 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/10 to-background" />

            <div className="container relative mx-auto px-4">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <Badge className="mb-6 bg-primary/10 text-primary border-primary/20 px-4 py-1.5 animate-pulse-subtle">Top 8 AI Photo Editing Services</Badge>
                    <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                        Our Most Popular <span className="text-primary">Photo Editing Services</span>
                    </h2>
                    <p className="text-xl text-muted-foreground leading-relaxed">
                        Based on <span className="font-semibold text-foreground">2.5M+ real edits</span>, these are our customers' top requests.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
                    {quickActions.map((action, i) => (
                        <Link key={i} href="/create">
                            <div className="rounded-3xl backdrop-blur-xl bg-card/40 border border-white/10 p-6 sm:p-8 h-full group hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500 cursor-pointer hover:scale-[1.02] hover:border-primary/30 relative overflow-hidden">
                                {/* Animated gradient background */}
                                <div className={`absolute inset-0 bg-gradient-to-br ${action.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                                <div className="relative z-10 flex flex-col h-full">
                                    <div className="flex items-start justify-between mb-6">
                                        <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${action.gradient} flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                                            <action.icon className="h-8 w-8 text-white" />
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-bold mb-3 group-hover:text-primary transition-colors">
                                        {action.title}
                                    </h3>
                                    <p className="text-muted-foreground mb-6 text-sm leading-relaxed flex-grow">
                                        {action.description}
                                    </p>
                                    <div className="flex items-center text-primary font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300 mt-auto">
                                        Start Now
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="text-center mt-16">
                    <p className="text-lg text-muted-foreground mb-6">
                        <span className="font-semibold text-foreground">Can't find what you need?</span> We handle any custom editing request.
                    </p>
                    <Link href="/dashboard">
                        <Button variant="outline" size="lg" className="h-12 px-8 rounded-full border-primary/20 hover:bg-primary/5 hover:text-primary gap-2">
                            Explore All Services
                            <ArrowRight className="h-4 w-4" />
                        </Button>
                    </Link>
                </div>
            </div>
        </section>
    );
}
